import { cachePolicy } from '@pn/core/cachePolicy';
import type { DataItemId } from '@pn/core/domain/data';
import type { Project } from '@pn/core/domain/project';
import type { WorkspaceItem } from '@pn/core/domain/workspace';
import { findOrThrow } from '@pn/core/utils/logic';
import { stackApiClient } from '@pn/services/api/stackApiClient';
import { apiStackProjectDetailsMapper } from './apiStackProjectDetailsMapper';
import { apiStackWorkspaceItemToWorkspaceItemMapper } from './apiStackWorkspaceItemMapper';
import type { StackProjectWithListIds } from './types';

const cache = new Map<string, StackProjectWithListIds>();

async function requestProjectDetails(
  stackProjectId: string,
  companyId: string
): Promise<StackProjectWithListIds> {
  if (cachePolicy.stackProjectDetails && cache.has(stackProjectId)) {
    return cache.get(stackProjectId)!;
  }

  const details = await stackApiClient.request<StackProjectWithListIds>(
    {
      url: `PetroNinjaAuth0/Projects/${stackProjectId}`,
    },
    companyId
  );

  if (cachePolicy.stackProjectDetails) cache.set(stackProjectId, details);

  return details;
}

/**
 * Returns the project, plus a function that builds the workspace item for the
 * project's list of a given source item; callers decide which lists they need.
 */
async function getProjectDetails(
  stackProjectId: string,
  companyId: string
): Promise<{
  project: Project;
  toWorkspaceItem: (sourceItem?: WorkspaceItem) => WorkspaceItem;
}> {
  const details = await requestProjectDetails(stackProjectId, companyId);
  const itemMapper = apiStackWorkspaceItemToWorkspaceItemMapper();

  return {
    project: apiStackProjectDetailsMapper.toDomainProject(details),
    toWorkspaceItem: (sourceItem) =>
      itemMapper.toWorkspaceItem(details, sourceItem),
  };
}

/* Current ids of the project's list built on the given source item. */
async function getListIds(
  stackProjectId: string,
  sourceItemId: WorkspaceItem['id'],
  companyId: string
): Promise<DataItemId[]> {
  const details = await requestProjectDetails(stackProjectId, companyId);

  return findOrThrow(
    details.lists,
    (list) => list.sourceLayerId === sourceItemId
  ).ids;
}

export const apiStackProjectDetailsProvider = { getProjectDetails, getListIds };
